import { type LocaleSiteContent, type SiteContent } from "../types/siteContent";

// Frontend fallback content.
//
// The normal homepage data flow is:
// 1. Admin edits a draft in `/admin`.
// 2. Admin publishes that draft through `/api/admin/content/publish`.
// 3. The public homepage reads the published snapshot from `/api/site-content`.
//
// This file is not the day-to-day content source. It exists so local
// development, first setup, or a temporary backend outage still renders a full
// page. Keep this shape synchronized with the server-side `siteContentSchema`.
const zhFallback: LocaleSiteContent = {
  brandMark: "M",
  brandName: "MAIPO",
  brandSub: "AI 辅助建筑建模",
  navFeatures: "功能",
  navPricing: "价格",
  navDownload: "下载",
  navFaq: "常见问题",
  navContact: "联系我们",
  contactQrUrl: "/wechat-internal-test-qr.jpg",
  contactQrAlt: "微信二维码",
  betaGroupQrUrl: "/wechat-internal-test-qr.jpg",
  betaGroupQrAlt: "微信群二维码",
  navLogin: "登录 / 注册",
  themeDark: "深色",
  themeLight: "浅色",
  heroEyebrow: "Architecture Workflow",
  heroTitle1: "麦普",
  heroTitle2: "全流程设计Agent",
  heroTitle3: "",
  heroCopy: "AI 辅助建模工具，打通概念草模、深化建模、CAD 出图到 BIM 落地的全流程，让方案设计从分散协作走向一体闭环。",
  heroSignals: ["文生建模", "平立剖生成", "渲染图生成"],
  heroDownloadCount: "下载数量",
  heroUserCount: "用户数量",
  heroStatsAriaLabel: "网站公开统计",
  heroVisualAriaLabel: "麦普 产品界面预览",
  heroImageAlt: "麦普产品界面预览图",
  downloadNow: "立即下载",
  viewFeatures: "查看功能",
  features: [
    {
      eyebrow: "Feature 01",
      title: "对话建模",
      body: "通过自然语言快速生成建筑草模，验证体量、空间关系与设计方向，把早期探索压缩到更短的时间里。",
      tags: ["概念生成", "草模验证", "创意辅助"],
      className: "feature-natural",
      isVisible: true,
      sortOrder: 1,
      mediaKind: "video",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "自然语言建模功能预览"
    },
    {
      eyebrow: "Feature 02",
      title: "自动出图",
      body: "通过提示词生成平面、立面、 剖面图，并自动补齐基础标注，减少重复出图和后期整理成本。",
      tags: ["平面", "立面", "剖面", "自动标注"],
      className: "feature-docs",
      isVisible: true,
      sortOrder: 2,
      mediaKind: "video",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "自动出图功能预览"
    },
    {
      eyebrow: "Feature 03",
      title: "渲染生成",
      body: "在任意设计阶段用模型截图生成更真实的建筑渲染图，用于方案汇报、沟通展示与 视觉验证。",
      tags: ["截图生图", "真实渲染", "方案表达"],
      className: "feature-render",
      isVisible: true,
      sortOrder: 3,
      mediaKind: "video",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "渲染生成功能预览"
    },
    {
      eyebrow: "Feature 04",
      title: "BIM 建模",
      body: "依托 Rhino/GH 原生插件，实现与 Revit 衔接，补齐方案与施工图衔接的核心短板，减少繁琐的手动 BIM 建模。",
      tags: ["Rhino/GH", "Revit 衔接", "构件深化"],
      className: "feature-bim",
      isVisible: true,
      sortOrder: 4,
      mediaKind: "image",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "BIM 建模功能预览"
    }
  ],
  downloadSectionEyebrow: "Download",
  downloadTitle: "选择适合你的使用方式。",
  downloadCopy: "先从免费试用开始，按建模与生图需求选择不同版本。安装包下载需要先登录普通用户账号。",
  plans: [
    {
      key: "Free",
      title: "免费版",
      price: "Free",
      body: "适合初次体验麦普的个人设计师。",
      lines: [
        { label: "内容", value: "有限积分，可试用基础 AI 建模能力。" },
        { label: "适用对象", value: "想快速验证工作流是否适配的个人用户。" },
        { label: "升级方式", value: "后续可升级到付费版本。" }
      ],
      cta: "免费下载"
    },
    {
      key: "Standard",
      title: "常规版",
      price: "¥60/月",
      body: "适合日常需要高频 AI 建模的建筑设计师。",
      lines: [
        { label: "内容", value: "建模积分不限，图生图积分有限。" },
        { label: "适用对象", value: "主要需求集中在日常建模提速的个人设计师。" },
        { label: "计费方式", value: "每月 ¥60。" }
      ],
      cta: "选择常规版"
    },
    {
      key: "Pro",
      title: "Pro 版",
      price: "¥99/月",
      body: "适合需要高强度建模与渲染生成的专业用户或团队。",
      lines: [
        { label: "内容", value: "建模积分不限，图生图积分不限。" },
        { label: "适用对象", value: "高强度建模、汇报表达和团队试点用户。" },
        { label: "计费方式", value: "每月 ¥99。" }
      ],
      cta: "选择 Pro"
    }
  ],
  faqSectionEyebrow: "FAQ",
  faqTitle: "常见问题",
  faqs: [
    { question: "麦普适合谁使用？", answer: "适合建筑设计师、建模设计师，以及需要快速完成方案建模、构件深化、分析和表达的设计团队。" },
    { question: "麦普可以直接生成完整建筑吗？", answer: "麦普可以辅助生成草模和建筑构件，但设计判断、方案取舍和最终成果仍由设计师控制。" },
    { question: "免费版有什么限制？", answer: "免费版提供有限积分，适合体验基础建模能力和验证工作流。" },
    { question: "常规版和 Pro 版有什么区别？", answer: "常规版建模积分不限但图生图积分有限，Pro 版建模和图生图积分都不限。" },
    { question: "是否支持建筑分析？", answer: "支持结合 GH、Ladybug 等工具进行建筑分析，并可辅助检查部分规范要求。" },
    { question: "是否可以生成平立剖？", answer: "可以通过提示词生成平面、立面、剖面，并自动完成基础标注。" },
    { question: "是否支持渲染图生成？", answer: "支持在任意阶段通过模型截图生成真实感渲染图，用于方案沟通和汇报表达。" },
    { question: "当前支持哪些系统？", answer: "首发阶段优先支持 Windows Preview，macOS 版本后续开放。" }
  ],
  finalEyebrow: "maipo",
  finalTitle: "开启你的创造之旅",
  finalAccent: "更快衔接起来。",
  finalCopy: "从草模生成到构件深化，再到分析、出图与表达，麦普的目标不是制造更热闹的 AI 概念，而是建立更顺手的专业工作流。",
  installVideoEyebrow: "INSTALLATION GUIDE",
  installVideoTitle: "安装与首次启动",
  installVideoCopy: "跟随视频完成麦普插件安装，并在 Rhino 中完成首次启动。",
  installVideoLabel: "麦普安装教程",
  aboutTitle: "About maipo",
  about: "麦普是南京市麦普建筑科技有限公司旗下的建筑科技品牌，聚焦建筑设计与 AI 技术融合，致力于为建筑师、设计机构和产业伙伴提供更高效的设计生产工具与数字化工作流解决方案。公司围绕概念生成、建模深化、图纸表达与交付协同持续研发，推动建筑行业从传统重复劳动走向智能化、一体化的新型设计方式。",
  contactTitle: "Connect",
  contactItems: [
    { label: "Bilibili", url: "https://www.bilibili.com/", external: true },
    { label: "小红书", url: "https://www.xiaohongshu.com/", external: true },
    { label: "抖音", url: "https://www.douyin.com/", external: true },
    { label: "YouTube", url: "https://www.youtube.com/", external: true },
    { label: "X / Twitter", url: "https://x.com/", external: true },
    { label: "Instagram", url: "https://www.instagram.com/", external: true },
    { label: "邮箱：hello@maipo.ai", url: "mailto:hello@maipo.ai", external: false }
  ],
  copyrightText: "Copyright 2026 maipo",
  footerMeta3: "ICP备案信息预留",
  record: "公安联网备案信息预留",
  terms: "Terms",
  termsUrl: "#top",
  privacy: "Privacy",
  privacyUrl: "#top"
};

const enFallback: LocaleSiteContent = {
  brandMark: "M",
  brandName: "MAIPO",
  brandSub: "AI-Assisted Architecture Modeling",
  navFeatures: "Features",
  navPricing: "Price",
  navDownload: "Download",
  navFaq: "FAQ",
  navContact: "Contact Us",
  contactQrUrl: "/wechat-internal-test-qr.jpg",
  contactQrAlt: "WeChat QR code",
  betaGroupQrUrl: "/wechat-internal-test-qr.jpg",
  betaGroupQrAlt: "WeChat beta group QR code",
  navLogin: "Sign in / Register",
  themeDark: "Dark",
  themeLight: "Light",
  heroEyebrow: "Architecture Workflow",
  heroTitle1: "MAIPO",
  heroTitle2: "AI for Architecture",
  heroTitle3: "",
  heroCopy: "An AI-assisted modeling tool that connects concept massing, detailed modeling, CAD drawings, and BIM delivery into one continuous workflow.",
  heroSignals: ["Text-to-model", "Auto drawings", "Render generation"],
  heroDownloadCount: "Downloads",
  heroUserCount: "Users",
  heroStatsAriaLabel: "Public site statistics",
  heroVisualAriaLabel: "maipo product preview",
  heroImageAlt: "maipo product interface preview",
  downloadNow: "Download Now",
  viewFeatures: "View Features",
  features: [
    {
      eyebrow: "Feature 01",
      title: "Prompted Modeling",
      body: "Generate building massing from natural language, validate scale and spatial logic, and compress early exploration into a much faster loop.",
      tags: ["Concept generation", "Massing validation", "Creative assistance"],
      className: "feature-natural",
      isVisible: true,
      sortOrder: 1,
      mediaKind: "video",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "Natural language modeling preview"
    },
    {
      eyebrow: "Feature 02",
      title: "Technical Drawings",
      body: "Generate plans, elevations, and sections from prompts with basic annotations filled in automatically to reduce repetitive drafting work.",
      tags: ["Plan", "Elevation", "Section", "Auto annotation"],
      className: "feature-docs",
      isVisible: true,
      sortOrder: 2,
      mediaKind: "video",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "Auto plans and sections preview"
    },
    {
      eyebrow: "Feature 03",
      title: "Render Generation",
      body: "Turn model screenshots into more realistic architectural renders at any design stage for reviews, presentations, and visual validation.",
      tags: ["Image-to-image", "Realistic render", "Visual storytelling"],
      className: "feature-render",
      isVisible: true,
      sortOrder: 3,
      mediaKind: "video",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "Render generation preview"
    },
    {
      eyebrow: "Feature 04",
      title: "BIM Modeling",
      body: "Built on native Rhino/GH plugins, maipo connects with Revit and closes the gap between design schemes and construction delivery.",
      tags: ["Rhino/GH", "Revit connection", "Component detailing"],
      className: "feature-bim",
      isVisible: true,
      sortOrder: 4,
      mediaKind: "image",
      mediaUrl: "",
      imageUrl: "",
      imageAlt: "BIM modeling preview"
    }
  ],
  downloadSectionEyebrow: "Download",
  downloadTitle: "Choose the plan that fits your workflow.",
  downloadCopy: "Start with a free trial, then pick the version that matches your modeling and rendering needs. Installer downloads require sign-in.",
  plans: [
    {
      key: "Free",
      title: "Free",
      price: "Free",
      body: "For individual designers trying maipo for the first time.",
      lines: [
        { label: "Includes", value: "Limited credits for core AI modeling workflows." },
        { label: "Best for", value: "Individuals validating whether the workflow fits their practice." },
        { label: "Upgrade path", value: "Move to a paid plan later when needed." }
      ],
      cta: "Download Free"
    },
    {
      key: "Standard",
      title: "Standard",
      price: "¥60/month",
      body: "For architects who need high-frequency AI modeling in daily work.",
      lines: [
        { label: "Includes", value: "Unlimited modeling credits, limited image-generation credits." },
        { label: "Best for", value: "Individual architects focused on accelerating everyday modeling tasks." },
        { label: "Billing", value: "¥60 per month." }
      ],
      cta: "Choose Standard"
    },
    {
      key: "Pro",
      title: "Pro",
      price: "¥99/month",
      body: "For professionals or teams needing intensive modeling and render generation.",
      lines: [
        { label: "Includes", value: "Unlimited modeling credits and unlimited image-generation credits." },
        { label: "Best for", value: "High-intensity modeling, presentation output, and team pilots." },
        { label: "Billing", value: "¥99 per month." }
      ],
      cta: "Choose Pro"
    }
  ],
  faqSectionEyebrow: "FAQ",
  faqTitle: "Frequently Asked Questions",
  faqs: [
    { question: "Who is maipo for?", answer: "maipo is designed for architects, modeling specialists, and design teams that need faster modeling, detailing, analysis, and presentation workflows." },
    { question: "Can maipo generate a complete building directly?", answer: "maipo can assist with massing and component generation, but design judgment, option selection, and final outputs remain under the designer's control." },
    { question: "What are the limits of the free plan?", answer: "The free plan provides limited credits and is intended for testing the core modeling workflow." },
    { question: "What is the difference between Standard and Pro?", answer: "Standard includes unlimited modeling credits with limited image-generation credits, while Pro removes limits for both." },
    { question: "Does maipo support building analysis?", answer: "Yes. It can work with tools such as Grasshopper and Ladybug for analysis and can assist with selected compliance checks." },
    { question: "Can it generate plans, elevations, and sections?", answer: "Yes. Prompt-based generation can produce plans, elevations, and sections with basic annotations." },
    { question: "Does it support render generation?", answer: "Yes. It can turn screenshots from any stage into realistic renders for reviews and presentations." },
    { question: "Which systems are supported right now?", answer: "The initial release prioritizes Windows Preview, with macOS support planned later." }
  ],
  finalEyebrow: "maipo",
  finalTitle: "Get started with MAIPO",
  finalAccent: "with less friction.",
  finalCopy: "From massing to detailing, then analysis, drawings, and presentation, maipo is built to create a more usable professional workflow rather than a louder AI concept.",
  installVideoEyebrow: "INSTALLATION GUIDE",
  installVideoTitle: "Install and launch MAIPO",
  installVideoCopy: "Follow the video to install the MAIPO plugin and complete the first launch in Rhino.",
  installVideoLabel: "MAIPO installation guide",
  aboutTitle: "About maipo",
  about: "Maipo is the architecture technology brand of Nanjing Maipo Architecture Technology Co., Ltd. We focus on combining architecture and AI to build more efficient design-production tools and digital workflow solutions for architects, studios, and industry partners. Our work centers on concept generation, model development, drawing production, and delivery collaboration, helping move architectural practice from repetitive manual work toward integrated intelligent workflows.",
  contactTitle: "Connect",
  contactItems: [
    { label: "Bilibili", url: "https://www.bilibili.com/", external: true },
    { label: "Xiaohongshu", url: "https://www.xiaohongshu.com/", external: true },
    { label: "Douyin", url: "https://www.douyin.com/", external: true },
    { label: "YouTube", url: "https://www.youtube.com/", external: true },
    { label: "X / Twitter", url: "https://x.com/", external: true },
    { label: "Instagram", url: "https://www.instagram.com/", external: true },
    { label: "Email: hello@maipo.ai", url: "mailto:hello@maipo.ai", external: false }
  ],
  copyrightText: "Copyright 2026 maipo",
  footerMeta3: "ICP filing pending company confirmation",
  record: "Public security filing pending company confirmation",
  terms: "Terms",
  termsUrl: "#top",
  privacy: "Privacy",
  privacyUrl: "#top"
};

export const defaultSiteContent: SiteContent = {
  assets: {
    heroMediaKind: "video",
    heroMediaUrl: "",
    heroImageUrl: "",
    featureImageUrls: [],
    videoUrl: "",
    installVideoUrl: "",
    installVideoPosterUrl: ""
  },
  zh: zhFallback,
  en: enFallback
};
